/**
 * @file Composable for hover-based section-action keyboard shortcuts on the surface (e.g. the "S"
 * shortcut that starts the slideshow from whichever section header is currently hovered). Mirrors
 * useOnSurfacePostHover, but for section headers. A section header and a post card can't be hovered
 * at once (distinct DOM regions), so shortcuts that share a key resolve by which one is hovered — see
 * addHoverOnSectionShortcuts.
 */
import { dir } from '@@/bits/current_dir'
import {
  isHoverShortcutsEnabled,
  shouldSkipHoverActionShortcut,
  trackHoverShortcutTriggered,
} from '@@/bits/surface_hover_shortcuts'
import { useSurfaceStore } from '@@/pinia/surface'
import { SectionActionType, useSurfaceGroupActionStore } from '@@/pinia/surface_group_action_store'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { storeToRefs } from 'pinia'
import type { KeyBindingMap } from 'tinykeys'

const useOnSurfaceSectionHeaderHover = (): {
  addHoverOnSectionShortcuts: (shortcuts: KeyBindingMap) => void
} => {
  const surfaceGroupActionStore = useSurfaceGroupActionStore()
  const { hoveredSectionId } = storeToRefs(surfaceGroupActionStore)
  const { isOverlayVisible, canUseSections } = storeToRefs(useSurfaceStore())
  const { postBeingEditedCid } = storeToRefs(useSurfacePostsStore())

  function createHoveredSectionShortcutHandler({
    action,
    keyLabel,
    analyticsAction,
  }: {
    action: SectionActionType
    keyLabel: string
    analyticsAction: string
  }): (event: KeyboardEvent) => void {
    return (event: KeyboardEvent): void => {
      if (
        shouldSkipHoverActionShortcut(event, {
          isBeingEdited: !!postBeingEditedCid.value,
          isOverlayVisible: isOverlayVisible.value,
        })
      )
        return
      if (!canUseSections.value) return
      if (hoveredSectionId.value == null) return
      if (!surfaceGroupActionStore.canPerformOnHoveredSection(action)) return
      event.preventDefault()
      trackHoverShortcutTriggered('section', keyLabel, analyticsAction)
      surfaceGroupActionStore.performOnHoveredSection(event, action)
    }
  }

  const handleOnKeydownS = createHoveredSectionShortcutHandler({
    action: SectionActionType.StartSlideshowFromThisSection,
    keyLabel: 's',
    analyticsAction: 'start slideshow from this section',
  })
  const handleOnKeydownC = createHoveredSectionShortcutHandler({
    action: SectionActionType.AddPost,
    keyLabel: 'c',
    analyticsAction: 'add post to section',
  })
  const handleOnKeydownL = createHoveredSectionShortcutHandler({
    action: SectionActionType.CopySectionBreakoutLink,
    keyLabel: 'l',
    analyticsAction: 'copy breakout room link',
  })
  const handleOnKeydownBackspace = createHoveredSectionShortcutHandler({
    action: SectionActionType.DeleteSection,
    keyLabel: 'backspace',
    analyticsAction: 'delete section',
  })
  const handleOnKeydownV = createHoveredSectionShortcutHandler({
    action: SectionActionType.StartFieldTripFromThisSection,
    keyLabel: 'v',
    analyticsAction: 'start virtual tour from this section',
  })

  const handleMoveSectionBefore = createHoveredSectionShortcutHandler({
    action: SectionActionType.MoveSectionBefore,
    keyLabel: 'move-section-before',
    analyticsAction: 'move section before',
  })
  const handleMoveSectionAfter = createHoveredSectionShortcutHandler({
    action: SectionActionType.MoveSectionAfter,
    keyLabel: 'move-section-after',
    analyticsAction: 'move section after',
  })

  /**
   * Horizontal arrows follow the visual direction rather than the physical key, mirroring how
   * SurfaceContainerTimeline's Shift+ArrowLeft/Right section navigation swaps for RTL: in RTL, the
   * "before" section renders to the right, so the physical Left key must move the hovered section
   * after (toward what's visually its right) and the physical Right key must move it before.
   */
  const handleMoveSectionOnArrowLeft = (event: KeyboardEvent): void => {
    if (dir() === 'rtl') {
      handleMoveSectionAfter(event)
    } else {
      handleMoveSectionBefore(event)
    }
  }
  const handleMoveSectionOnArrowRight = (event: KeyboardEvent): void => {
    if (dir() === 'rtl') {
      handleMoveSectionBefore(event)
    } else {
      handleMoveSectionAfter(event)
    }
  }

  /**
   * Add hover-aware shortcuts for section header actions to the supplied key bindings.
   *
   * When a section header is hovered, the section handler runs first and falls through to any
   * existing binding only if it did not act.
   *
   * @param shortcuts The key binding map to mutate.
   */
  function addHoverOnSectionShortcuts(shortcuts: KeyBindingMap): void {
    if (!isHoverShortcutsEnabled) return
    /**
     * S/C/L/Backspace/V are shared with post-hover shortcuts and the container's new-post "C". Since
     * a section header and a post card can't be hovered simultaneously, compose over any existing
     * binding: run the section handler first, and fall through to the previous handler only when no
     * section was hovered (the section handler preventDefault()s exactly when it acts).
     */
    const bindWithFallthrough = (key: string, handler: (event: KeyboardEvent) => void): void => {
      const existing = shortcuts[key]
      shortcuts[key] = (event: KeyboardEvent): void => {
        handler(event)
        if (!event.defaultPrevented && typeof existing === 'function') existing(event)
      }
    }
    bindWithFallthrough('KeyS', handleOnKeydownS)
    bindWithFallthrough('KeyC', handleOnKeydownC)
    bindWithFallthrough('KeyL', handleOnKeydownL)
    bindWithFallthrough('Backspace', handleOnKeydownBackspace)
    bindWithFallthrough('KeyV', handleOnKeydownV)
    bindWithFallthrough('ArrowUp', handleMoveSectionBefore)
    bindWithFallthrough('ArrowLeft', handleMoveSectionOnArrowLeft)
    bindWithFallthrough('ArrowDown', handleMoveSectionAfter)
    bindWithFallthrough('ArrowRight', handleMoveSectionOnArrowRight)
  }

  return { addHoverOnSectionShortcuts }
}

export default useOnSurfaceSectionHeaderHover
